import { FC, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { RothoSSOSettings, useAuthContext } from "../../context/authContext";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useI18n from "../../hooks/useTranslations";
import { isClientError } from "../../http/errors";
import { useConfigurationSharingAccessesApi } from "../../http/useConfigurationSharingAccesses";
import Button from "../ui/Button";
import SubmitBar from "../ui/SubmitBar";
import styles from "./ManageConfigurationInvite.module.css";

type ManageConfigurationInviteProps = {
  settings: RothoSSOSettings;
};

const ManageConfigurationInvite: FC<ManageConfigurationInviteProps> = ({
  settings,
}: ManageConfigurationInviteProps) => {
  const i18n = useI18n();
  const auth = useAuthContext();
  const configurationSharingAccessesApi = useConfigurationSharingAccessesApi();

  const { token } = useParams<{ token: string }>();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clicked, setClicked] = useState<boolean>(false);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isAcceptCallback = searchParams.get("accepted") === "true";

  const navigate = useNavigate();

  const acceptInvite = () => {
    if (token) {
      setIsSubmitting(true);
      configurationSharingAccessesApi
        .acceptConfigurationSharingAccessInvite(token)
        .then(({ configurationId }) => {
          setClicked(true);
          dispatch(
            showSuccessNotification(
              i18n.translation,
              i18n.translation.configurations.invitationStatus.statusChanges.accept,
            ),
          );
          navigate(`/configurations/${configurationId}`);
        })
        .catch((err) => dispatch(showErrorNotification(i18n, err)))
        .finally(() => setIsSubmitting(false));
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }

    if (isAcceptCallback && auth.identity && !clicked) {
      acceptInvite();
    }
  }, [auth.identity]);

  const { data: configurationData, error: configurationDataError } =
    configurationSharingAccessesApi.useConfigurationSharingAccessInviteConfigurationData(token ?? "");
  const inviteNotFound = isClientError(configurationDataError);
  const invitePageTranslation = {
    ...i18n.translation.common.invitePage,
    ...i18n.translation.configurations.invitePage,
  };
  const authorName =
    [configurationData?.author.firstName, configurationData?.author.lastName].filter(Boolean).join(" ") ||
    invitePageTranslation.fallbackAuthorName;

  const submit = (accept: boolean) => {
    setIsSubmitting(true);

    if (accept) {
      if (!auth.identity) {
        if (!isAcceptCallback) {
          const acceptUrl = new URL(window.location.href);
          acceptUrl.searchParams.set("accepted", "true");
          acceptUrl.searchParams.set("email_locked", "1");
          configurationData?.invitedEmail && acceptUrl.searchParams.set("email", configurationData.invitedEmail);
          window.location.href = `${settings.iamUrl}?redirect_uri=${acceptUrl.toString()}`;
        }
        auth.init(true);
        return;
      } else {
        acceptInvite();
        return;
      }
    } else {
      if (token) {
        configurationSharingAccessesApi
          .rejectConfigurationSharingAccessInvite(token)
          .then(() => {
            setClicked(true);
            dispatch(
              showSuccessNotification(
                i18n.translation,
                i18n.translation.configurations.invitationStatus.statusChanges.rejected,
              ),
            );
          })
          .catch((err) => dispatch(showErrorNotification(i18n, err)))
          .finally(() => setIsSubmitting(false));
      }
    }
  };

  return (
    <div className={styles.centerContent}>
      <section className={styles.card}>
        <div className={styles.inviteHero}>
          <span className={styles.badge}>{invitePageTranslation.badge}</span>
          <h2 className={styles.configurationName}>
            {configurationData?.name ??
              (inviteNotFound
                ? invitePageTranslation.notFoundTitle
                : invitePageTranslation.loadingConfigurationDetails)}
          </h2>
          {!inviteNotFound ? (
            <p className={styles.lead}>
              <strong>{authorName}</strong> {invitePageTranslation.collaborationLead}
            </p>
          ) : undefined}
        </div>

        {!inviteNotFound ? (
          <>
            <div className={styles.detailsGrid}>
              <div className={styles.detailCard}>
                <span className={styles.detailLabel}>{invitePageTranslation.invitedBy}</span>
                <span className={styles.detailValue}>{authorName}</span>
              </div>
              <div className={styles.detailCard}>
                <span className={styles.detailLabel}>{invitePageTranslation.invitationSentTo}</span>
                <span className={styles.detailValue}>
                  {configurationData?.invitedEmail ?? invitePageTranslation.notAvailable}
                </span>
              </div>
            </div>

            <div className={styles.summary}>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>{invitePageTranslation.configurationName}</span>
                <span className={styles.summaryValue}>
                  {configurationData?.name ?? invitePageTranslation.notAvailable}
                </span>
              </div>
              {configurationData?.description ? (
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>{invitePageTranslation.description}</span>
                  <p className={styles.description}>{configurationData.description}</p>
                </div>
              ) : undefined}
            </div>
          </>
        ) : undefined}

        <div className={styles.notice}>
          {inviteNotFound
            ? invitePageTranslation.inviteNotFound
            : !configurationData
              ? ""
              : !configurationData.actionRequired
                ? invitePageTranslation.inviteNoLongerAvailable
                : auth.identity
                  ? invitePageTranslation.acceptSignedIn
                  : invitePageTranslation.acceptSignedOut}
        </div>

        {!inviteNotFound ? (
          <div className={styles.actions}>
            <SubmitBar>
              <Button
                buttonProps={{
                  disabled: isSubmitting || clicked || !configurationData?.actionRequired,
                  onClick: () => submit(true),
                }}
              >
                {invitePageTranslation.acceptAction}
              </Button>
              <Button
                type="secondary"
                buttonProps={{
                  disabled: isSubmitting || clicked || !configurationData?.actionRequired,
                  onClick: () => submit(false),
                }}
              >
                {invitePageTranslation.rejectAction}
              </Button>
            </SubmitBar>
          </div>
        ) : undefined}
      </section>
    </div>
  );
};

export default ManageConfigurationInvite;
